import { useMemo, useState } from "react";
import { ListPager, usePaged } from "@/components/ListPager";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, ClipboardList, Pencil, Trash2, Eye, FileSpreadsheet, Upload } from "lucide-react";
import { ExportRunsDialog } from "./ExportRunsDialog";
import { ImportRunsDialog } from "./ImportRunsDialog";
import { useAuth } from "@/context/AuthContext";
import { DatePicker } from "@/components/DatePicker";
import { del, errorMessage } from "@/lib/api";
import { fetchLocksmiths, fetchRuns, useAsync } from "@/lib/queries";
import { RUN_STATUSES, formatDate, formatTime, progressOf } from "@/lib/constants";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { UserAvatar } from "@/components/UserAvatar";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const Filters = ({ f, setF, locksmiths, openJobs }) => (
  <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_170px_200px_150px_150px]">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input placeholder="Search postcode, address, locksmith, officer" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="pl-9" aria-label="Search runs" data-testid="runs-search-input" />
    </div>
    <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
      <SelectTrigger data-testid="runs-status-filter" aria-label="Status filter"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All statuses</SelectItem>
        {RUN_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={f.locksmithId} onValueChange={(v) => setF({ ...f, locksmithId: v })}>
      <SelectTrigger data-testid="runs-locksmith-filter" aria-label="Locksmith filter"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All locksmiths</SelectItem>
        {openJobs && <SelectItem value="open" data-testid="runs-locksmith-filter-open">Open jobs (not taken yet)</SelectItem>}
        {locksmiths.map((l) => <SelectItem key={l.id} value={l.id}>{l.full_name || l.email}</SelectItem>)}
      </SelectContent>
    </Select>
    <DatePicker value={f.from} onChange={(v) => setF({ ...f, from: v })} placeholder="From date" clearable aria-label="From date" testId="runs-from-date" />
    <DatePicker value={f.to} onChange={(v) => setF({ ...f, to: v })} placeholder="To date" clearable aria-label="To date" testId="runs-to-date" />
  </div>
);

const matches = (r, q) => {
  if (!q) return true;
  const hay = [r.locksmith?.full_name, r.locksmith?.email, r.officer?.full_name, ...r.run_addresses.flatMap((a) => [a.postcode, a.full_address])].join(" ").toLowerCase();
  return hay.includes(q.toLowerCase().trim());
};

export default function Runs() {
  const navigate = useNavigate();
  const [f, setF] = useState({ q: "", status: "all", locksmithId: "all", from: "", to: "" });
  const { profile } = useAuth();
  const [toDelete, setToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const locksmiths = useAsync(() => fetchLocksmiths(), []);
  const runs = useAsync(() => fetchRuns(f), [f.status, f.locksmithId, f.from, f.to]);
  const rows = useMemo(() => (runs.data || []).filter((r) => matches(r, f.q)), [runs.data, f.q]);
  const pager = usePaged(rows);

  const remove = async () => {
    try {
      await del(`/runs/${toDelete.id}`);
      toast.success("Warrant run deleted");
      runs.reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
  };

  return (
    <div className="space-y-6" data-testid="runs-page">
      <PageHeader eyebrow="Operations" title="Warrant Runs" description="List of your teams runs"
        actions={<div className="flex flex-wrap gap-2">
          {<Button variant="outline" onClick={() => setImporting(true)} data-testid="import-runs-button"><Upload className="mr-1 h-4 w-4" /> Import runs</Button>}
          <Button variant="outline" onClick={() => setExporting(true)} data-testid="export-runs-button"><FileSpreadsheet className="mr-1 h-4 w-4" /> Export runs</Button>
          <Button asChild className="bg-blue-700 hover:bg-blue-800" data-testid="add-new-run-button"><Link to="/manager/runs/new"><Plus className="mr-1 h-4 w-4" /> Add New Warrant Run</Link></Button>
        </div>} />
      {exporting && <ExportRunsDialog open onOpenChange={setExporting} locksmiths={locksmiths.data || []} initial={f} />}
      {importing && <ImportRunsDialog open onOpenChange={setImporting} onCreated={runs.reload} />}
      <Filters f={f} setF={setF} locksmiths={locksmiths.data || []} openJobs={profile?.company?.basic_plan !== true} />
      {runs.error && <ErrorState message={runs.error} onRetry={runs.reload} />}
      {runs.loading && !runs.data && <LoadingRows />}
      {runs.data && !rows.length && <EmptyState icon={ClipboardList} title="No warrant runs found" description="Adjust your filters or create a new run." testId="runs-empty" />}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <Table data-testid="runs-table">
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Date</TableHead><TableHead>Start</TableHead><TableHead>Locksmith</TableHead><TableHead>Officer</TableHead>
              <TableHead className="w-[180px]">Progress</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pager.items.map((r) => {
                const p = progressOf(r.run_addresses);
                return (
                  <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/manager/runs/${r.id}`)} data-testid={`run-row-${r.id}`}>
                    <TableCell className="whitespace-nowrap font-medium">{formatDate(r.run_date)}</TableCell>
                    <TableCell className="font-data whitespace-nowrap text-sm">{formatTime(r.start_time)}</TableCell>
                    <TableCell>{r.locksmith_id ? (
                      <span className="flex items-center gap-2"><UserAvatar userId={r.locksmith_id} stamp={r.locksmith?.avatar_updated_at} name={r.locksmith?.full_name} className="h-7 w-7 bg-slate-100 text-slate-400" testId={`run-avatar-${r.id}`} />{r.locksmith?.full_name || r.locksmith?.email}</span>
                    ) : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid={`run-open-${r.id}`}>Open job</span>}</TableCell>
                    <TableCell>{r.officer?.full_name}</TableCell>
                    <TableCell><ProgressBar done={p.done} total={p.total} compact /></TableCell>
                    <TableCell><StatusBadge status={r.status} testId={`run-status-${r.id}`} /></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" aria-label="View run" asChild data-testid={`view-run-${r.id}`}><Link to={`/manager/runs/${r.id}`}><Eye className="h-4 w-4" /></Link></Button>
                        <Button size="icon" variant="ghost" aria-label="Edit run" asChild data-testid={`edit-run-${r.id}`}><Link to={`/manager/runs/${r.id}/edit`}><Pencil className="h-4 w-4" /></Link></Button>
                        <Button size="icon" variant="ghost" aria-label="Delete run" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setToDelete(r)} data-testid={`delete-run-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {rows.length > 0 && <ListPager pager={pager} label="runs" testId="runs-pager" />}
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Delete warrant run?"
        description={toDelete ? `This permanently deletes the run on ${formatDate(toDelete.run_date)} for ${toDelete.locksmith?.full_name || "this locksmith"}, including all ${toDelete.run_addresses.length} addresses and notes.` : ""}
        confirmLabel="Delete run" onConfirm={remove} />
    </div>
  );
}
